import { Select, Space, Tag, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import type { AgentKind } from './agentApi'
import { useAgentIndex } from './useAgentIndex'

interface AgentSelectProps {
  /** antd `Form.Item` o'zi uzatadi — qo'lda berilmaydi */
  value?: string | null
  onChange?: (value: string | null) => void
  disabled?: boolean
  /**
   * Tozalash tugmasi ko'rinsinmi.
   *
   * ⚠️ TAHRIRLASHDA `false` BO'LISHI SHART va bu bezak emas. Backend
   * egalik maydonini `COALESCE` bilan yozadi
   * (`SubscriberRepository.update`), ya'ni `null` «tegilmasin» degani.
   * Tozalash tugmasi qoldirilsa operator maydonni bo'shatib saqlardi va
   * agent JOYIDA QOLARDI — panel «bajarildi» deb turgan holda. Jimgina
   * yolg'on, va uni faqat bazaga qarab sezish mumkin.
   */
  clearable?: boolean
  /**
   * O'rin egallovchi matn.
   *
   * ⚠️ Ro'yxatning O'ZI uchun emas, KONTEKST uchun. Formada standart
   * matn («Biriktirilmagan») to'g'ri, filtr panelida esa u yolg'on
   * bo'lardi: u yerda bo'sh tanlov «biriktirilmagan abonentlar» degani
   * emas, «agent bo'yicha filtrlanmayapti» degani.
   */
  placeholder?: string
  /**
   * Ro'yxatni tugun TURI bilan cheklaydi.
   *
   * ⚠️ Turlar ikkita va ular KESISHMAYDI (`AGENT_KINDS`), ya'ni bu
   * yerda ro'yxat aynan so'ralganini beradi.
   */
  kinds?: readonly AgentKind[]
}

/**
 * Agent (yoki reseller) tanlash — abonent kimga biriktirilishi.
 *
 * <h2>Nega bu tanlov umuman paydo bo'ldi</h2>
 * `subscriber.agent_id` filial daromadi zanjirining BIRINCHI bo'g'ini
 * (`abonent → agent → filial`). Staging'da o'lchandi: 74 ta abonentdan
 * `agent_id` to'ldirilgani 0 ta, ya'ni daromad hisoboti hammasini
 * «taqsimlanmagan» savatiga tashlardi. Backend bu maydonni ANCHADAN BERI
 * qabul qiladi (`SubscriberDraft.agentId`), lekin panelda uni beradigan
 * joy yo'q edi — zanjir aynan shu yerda uzilgan edi.
 *
 * <h2>⚠️ BO'SH QOLDIRISH — NORMAL, XATO EMAS</h2>
 * Ikki sababdan:
 * <ul>
 *   <li>«operator to'g'ridan-to'g'ri sotdi» — haqiqiy holat va u
 *       yo'qolmasligi kerak;</li>
 *   <li>chaqiruvchining O'ZI agent bo'lsa, server maydonni o'zi
 *       to'ldiradi (`SubscriberOwnership.resolveForCreate`) — ya'ni bo'sh
 *       yuborish «egasiz» degani emas.</li>
 * </ul>
 * Shuning uchun bu yerda `required` YO'Q va bo'sh holat qizil emas.
 *
 * <h2>⚠️ YOPIQ RO'YXAT, erkin matn emas</h2>
 * Qiymat — `platform.org_node.id`. Qo'lda yozilgan matn hech qanday
 * tugunga tegishli bo'lmasdi, server esa uni 404 bilan rad etardi va
 * operator sababini ko'rmasdi (`SiteSelect` dagi bilan bir xil qaror).
 *
 * <h2>⚠️ Ro'yxatda FAQAT DOIRADAGI agentlar</h2>
 * Manba `useAgentIndex` va u serverning doira predikatidan o'tadi —
 * batafsil o'sha hook'da. Bu yerda filtr TAKRORLANMAYDI.
 *
 * <h2>⚠️ Tanlangan qiymat ro'yxatda bo'lmasligi mumkin</h2>
 * Abonent doiramizdan tashqaridagi agentga biriktirilgan bo'lsa (yoki
 * `agent.read` huquqi bo'lmasa) tanlov ro'yxatida u YO'Q. Uni jimgina
 * tashlab yuborish — bog'lanishni o'chirish bilan teng bo'lardi, ya'ni
 * aynan tuzatilayotgan nuqsonni qaytarish. Shuning uchun ogohlantirish
 * chiqadi va qiymat forma do'konida QOLADI.
 */
// Abonentni RESELLER qo'shadi; agent faqat pul bilan ishlaydi (ADR-029).
export const RESELLER_KINDS: readonly AgentKind[] = ['RESELLER']

export function AgentSelect({
  value,
  onChange,
  disabled,
  clearable = true,
  placeholder,
  kinds,
}: AgentSelectProps) {
  const { t } = useTranslation()
  const { agents, loading, reliable } = useAgentIndex()

  // ⚠️ FAOL AGENTLAR OLDINDA. Reyestrda yopilgan tugun ko'p bo'lishi
  //    odatiy (jonli Staging'da 29 tadan 24 tasi ACTIVE emas) va ular
  //    aralash turganda operator kerakli agentni ko'zi bilan qidirardi.
  //    Yopilganlar OLIB TASHLANMAYDI: ularga bog'langan abonent bor va
  //    u holda o'sha abonentni filtrlab bo'lmasdi.
  // ⚠️ `kind` `null` bo'lishi mumkin (eski yozuv). Bunday tugun
  //    cheklov berilganda CHIQARILMAYDI — «turi noma'lum» ni
  //    «so'ralgan turga kiradi» deb hisoblash yolg'on bo'lardi.
  const scoped = kinds === undefined
    ? agents
    : agents.filter((a) => a.kind !== null && a.kind !== undefined && kinds.includes(a.kind))

  const ordered = [...scoped].sort((a, b) => {
    const byState = Number(b.state === 'ACTIVE') - Number(a.state === 'ACTIVE')
    return byState !== 0 ? byState : a.displayName.localeCompare(b.displayName)
  })

  const options = ordered.map((agent) => ({
    value: agent.id,
    label: (
      <Space size={4}>
        {/* Daraxtdagi chuqurlik — ost-reseller ustkisidan ajralib tursin */}
        {agent.depth > 1 ? (
          <Typography.Text type="secondary">{' '.repeat((agent.depth - 1) * 2)}</Typography.Text>
        ) : null}
        <span>{agent.displayName}</span>
        <Tag>{agent.code}</Tag>
        {/* ⚠️ Holat KO'RSATILADI, lekin TANLOV TO'SILMAYDI: server
            to'xtatilgan tugunni rad etmaydi (`PartyNodeLookup.exists`
            holatga qaramaydi), va panelning serverdan qattiqroq bo'lishi
            operatorni tushunarsiz to'siqqa urardi. */}
        {agent.state !== 'ACTIVE' ? (
          <Tag color="default">{t(`agentState.${agent.state}`)}</Tag>
        ) : null}
      </Space>
    ),
    title: `${agent.displayName} ${agent.code}`,
  }))

  // ⚠️ `reliable` SHART: usiz ogohlantirish ro'yxat YUKLANAYOTGANDA ham
  // chiqardi (o'sha lahzada `agents` bo'sh) — ya'ni tahrirlash oynasi har
  // ochilganda bir zumga «agent ro'yxatda yo'q» deb yonib o'chardi.
  // `useSiteIndex` dagi «bo'sh ro'yxatning uch sababi» qoidasining
  // aynan o'zi.
  const missing = reliable && Boolean(value) && !agents.some((agent) => agent.id === value)

  return (
    <>
      <Select<string | null>
        allowClear={clearable}
        showSearch
        value={value ?? null}
        onChange={(next) => onChange?.(next ?? null)}
        disabled={disabled}
        loading={loading}
        placeholder={placeholder ?? t('subscriber.agent.placeholder')}
        options={options}
        optionFilterProp="title"
        style={{ width: '100%' }}
        notFoundContent={reliable ? t('subscriber.agent.empty') : t('subscriber.agent.unavailable')}
      />
      {missing ? (
        <Typography.Text type="warning" style={{ fontSize: 12 }}>
          {t('subscriber.agent.selectMissing')}
        </Typography.Text>
      ) : null}
    </>
  )
}
